import { FormattedMessage, useIntl } from "react-intl";
import React, { ComponentPropsWithRef, useId, useRef } from "react";
import { clsx } from "clsx";

type SearchInputProps = ComponentPropsWithRef<"input"> & {
  onClear?: () => void;
  incremental?: boolean;
  inputSize?: "small" | "large";
  noBorder?: boolean;
};

function SearchInput(
  {
    className,
    onClear,
    incremental,
    inputSize,
    noBorder,
    ...inputProps
  }: SearchInputProps,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const id = useId();

  function clearInput() {
    const input = containerRef.current?.querySelector("input");
    if (input) {
      // Uncontrolled
      if (inputProps.value === undefined) {
        input.value = "";
      }
      // Controlled
      // Looked at simulating an onChange empty string and wasn't sure it's what
      // we want. Could also see user wanting to handle clear differently from
      // typing.
      if (onClear) {
        onClear();
      }
      input.focus();
    }
  }

  return (
    <div
      className={clsx(
        "vst-search-input-pill",
        incremental && "vst-search-input-mode-incremental",
        inputSize === "small" && "vst-search-input-size-small",
        inputSize === "large" && "vst-search-input-size-large",
        noBorder && "vst-search-input-variant-on-dark-bg",
        className
      )}
      ref={containerRef}
    >
      <label htmlFor={id}>
        <FormattedMessage defaultMessage="Search" />
      </label>
      <input {...inputProps} id={id} type="search" ref={ref} />
      <button
        type="button"
        aria-label={intl.formatMessage({ defaultMessage: "Clear" })}
        data-tooltip-content={intl.formatMessage({ defaultMessage: "Clear" })}
        onClick={() => clearInput()}
      />
      <button
        type="submit"
        aria-label={intl.formatMessage({ defaultMessage: "Search" })}
      />
    </div>
  );
}

const ForwardedSearchInput = React.forwardRef<
  HTMLInputElement,
  SearchInputProps
>(SearchInput);
export default ForwardedSearchInput;
